import { useMemo } from 'react'
import { motion } from 'motion/react'
import { ProductCard } from '@/entities/product/ui'
import { useTelegram } from '@/shared/lib/telegram'
import { useAddToCart } from '@/features/cart/api/use-cart'
import { useNavigate } from '@tanstack/react-router'
import { useTeawareCategories } from '@/entities/product/api/use-categories'
import { useCartNotification } from '@/widgets/cart-notification'
import type { Teaware, TeawareCategory, TeawareSubcategory } from '@/entities/product'

export interface CatalogGroupedTeawareSectionsProps {
  /** Список посуды для группировки */
  teaware: Teaware[]
  /** Состояние загрузки */
  isLoading?: boolean
  /** ID выбранной категории (если выбрана) - показываем только её подкатегории */
  selectedCategoryId?: string
  /** ID выбранной подкатегории (если выбрана) - показываем только её */
  selectedSubcategoryId?: string
}

/**
 * Секция каталога (категория с подкатегориями)
 */
interface CatalogSection {
  category: TeawareCategory
  /** Товары напрямую в категории (без подкатегории) */
  items: Teaware[]
  /** Подкатегории с товарами */
  subsections: Array<{
    subcategory: TeawareSubcategory
    items: Teaware[]
  }>
}

/**
 * Группирует товары по категориям и подкатегориям
 * Простая логика: сравнение по ID
 */
function groupProductsByCategories(
  products: Teaware[],
  categories: TeawareCategory[]
): CatalogSection[] {
  // Сортируем категории по orderIndex/sortOrder
  const sortedCategories = [...categories].sort((a, b) => {
    const orderA = a.orderIndex ?? a.sortOrder ?? 999
    const orderB = b.orderIndex ?? b.sortOrder ?? 999
    return orderA - orderB
  })

  return sortedCategories.map((category) => {
    // Товары в этой категории
    const categoryProducts = products.filter((p) => p.categoryId === category.id)

    // Товары без подкатегории (напрямую в категории)
    const directItems = categoryProducts.filter((p) => !p.subcategoryId)

    // Сортируем подкатегории
    const sortedSubcategories = [...(category.subcategories || [])].sort((a, b) => {
      const orderA = a.orderIndex ?? a.sortOrder ?? 999
      const orderB = b.orderIndex ?? b.sortOrder ?? 999
      return orderA - orderB
    })

    // Группируем по подкатегориям
    const subsections = sortedSubcategories.map((subcategory) => ({
      subcategory,
      items: categoryProducts.filter((p) => p.subcategoryId === subcategory.id),
    }))

    return {
      category,
      items: directItems,
      subsections,
    }
  })
}

/**
 * Виджет: Группировка каталога посуды по секциям
 * 
 * Отображает посуду в секциях с заголовками и декоративными элементами.
 * Показывает все категории из базы данных в порядке sortOrder.
 * Использует унифицированный ProductCard.
 */
export function CatalogGroupedTeawareSections({
  teaware,
  isLoading = false,
  selectedCategoryId,
  selectedSubcategoryId,
}: CatalogGroupedTeawareSectionsProps) {
  const navigate = useNavigate()
  const { hapticFeedback } = useTelegram()
  const addToCart = useAddToCart()
  const cartNotification = useCartNotification()

  // Получаем категории с бэкенда
  const { data: categoriesData, isLoading: categoriesLoading } = useTeawareCategories()
  const allCategories = categoriesData?.data ?? []
  
  // Если выбрана категория - фильтруем только её
  const categories = useMemo(() => {
    if (selectedCategoryId) {
      return allCategories.filter(cat => cat.id === selectedCategoryId)
    }
    return allCategories
  }, [allCategories, selectedCategoryId])

  // Фильтруем невалидные товары
  const validTeaware = useMemo(
    () => teaware.filter((t): t is Teaware => t != null && t.id != null),
    [teaware]
  )

  // Группируем товары по категориям
  const sections = useMemo(
    () => groupProductsByCategories(validTeaware, categories),
    [validTeaware, categories]
  )

  const handleProductClick = (item: Teaware) => {
    navigate({
      to: '/product/$productId',
      params: { productId: item.id },
    })
  }

  const handleAddToCart = (item: Teaware) => {
    hapticFeedback.impactOccurred('medium')
    addToCart.mutate({
      productType: 'teaware',
      productId: item.id,
      quantity: 1,
    }, {
      onSuccess: (result) => {
        cartNotification.show({
          productName: item.name,
          productImage: item.images?.[0]?.url,
          cartItemId: result.data?.id,
        })
        hapticFeedback.notificationOccurred('success')
      },
    })
  }

  // Skeleton загрузки
  if (isLoading || categoriesLoading) {
    return (
      <div className="px-4 space-y-8">
        {[1, 2, 3].map((i) => (
          <div key={i} className="space-y-4">
            <div className="h-6 bg-gray-200 dark:bg-dark-elevated rounded animate-pulse w-48" />
            <div className="grid grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((j) => (
                <div key={j} className="h-64 bg-gray-200 dark:bg-dark-elevated rounded-[16px] animate-pulse" />
              ))}
            </div>
          </div>
        ))}
      </div>
    )
  }

  // Фильтруем пустые секции
  const nonEmptySections = sections.filter(
    (s) => s.items.length > 0 || s.subsections.some((sub) => sub.items.length > 0)
  )

  if (nonEmptySections.length === 0) {
    return null
  }

  // Если выбрана подкатегория - показываем плоский список
  if (selectedSubcategoryId) {
    return (
      <div className="px-4 pb-6">
        <div className="grid grid-cols-2 gap-4 gap-y-5">
          {validTeaware.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: Math.min(index * 0.05, 0.5) }}
              className="w-full min-w-0"
            >
              <ProductCard
                product={item}
                onClick={() => handleProductClick(item)}
                onAddToCart={() => handleAddToCart(item)}
                showAddToCart
                showFavorite
                onHapticFeedback={(type) => hapticFeedback.impactOccurred(type || 'light')}
                className="w-full"
              />
            </motion.div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 space-y-8 pb-6">
      {nonEmptySections.map((section, sectionIndex) => (
        <motion.div
          key={section.category.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: Math.min(sectionIndex * 0.1, 0.5) }}
          className="space-y-4"
        >
          {/* Заголовок категории - скрываем если выбрана только одна категория */}
          {!selectedCategoryId && (
            <div>
              <h3 className="text-[20px] font-bold text-ink dark:text-white mb-1">
                {section.category.name}
              </h3>
              <div className="h-0.5 w-12 bg-primary dark:bg-primary-dark rounded-full" />
            </div>
          )}

          {/* Товары напрямую в категории */}
          {section.items.length > 0 && (
            <div className="grid grid-cols-2 gap-4 gap-y-5">
              {section.items.map((item, itemIndex) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.2,
                    delay: Math.min(itemIndex * 0.03, 0.3),
                  }}
                  className="w-full min-w-0"
                >
                  <ProductCard
                    product={item}
                    onClick={() => handleProductClick(item)}
                    onAddToCart={() => handleAddToCart(item)}
                    showAddToCart
                    showFavorite
                    onHapticFeedback={(type) => hapticFeedback.impactOccurred(type || 'light')}
                    className="w-full"
                  />
                </motion.div>
              ))}
            </div>
          )}

          {/* Подкатегории */}
          {section.subsections.map((subsection) => {
            if (subsection.items.length === 0) return null

            return (
              <div key={subsection.subcategory.id} className="space-y-3 mt-6">
                {/* Заголовок подкатегории */}
                <div>
                  <h4 className="text-[16px] font-semibold text-ink dark:text-white">
                    {subsection.subcategory.name}
                  </h4>
                  {subsection.subcategory.description && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      {subsection.subcategory.description}
                    </p>
                  )}
                </div>

                {/* Товары в подкатегории */}
                <div className="grid grid-cols-2 gap-4 gap-y-5">
                  {subsection.items.map((item, itemIndex) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.2,
                        delay: Math.min(itemIndex * 0.03, 0.3),
                      }}
                      className="w-full min-w-0"
                    >
                      <ProductCard
                        product={item}
                        onClick={() => handleProductClick(item)}
                        onAddToCart={() => handleAddToCart(item)}
                        showAddToCart
                        showFavorite
                        onHapticFeedback={(type) => hapticFeedback.impactOccurred(type || 'light')}
                        className="w-full"
                      />
                    </motion.div>
                  ))}
                </div>
              </div>
            )
          })}
        </motion.div>
      ))}
    </div>
  )
}
